import { formatEur } from '@fjalim/shared'
import type { Locale } from '../api/types'

/**
 * Locale-independent formatting. Some browsers (headless Chromium builds, minimal ICU)
 * have no 'sq' data and silently format Albanian as English ("€0.1788", "Nov 3, 2026").
 * When Intl cannot do Albanian, these helpers fall back to explicit tables.
 */

const SQ_TAG = 'sq-AL'
const NBSP = String.fromCharCode(160)

export function intlSupportsAlbanian(): boolean {
  try {
    return (
      Intl.NumberFormat.supportedLocalesOf(SQ_TAG).length > 0 &&
      Intl.DateTimeFormat.supportedLocalesOf(SQ_TAG).length > 0 &&
      Intl.PluralRules.supportedLocalesOf(SQ_TAG).length > 0
    )
  } catch {
    return false
  }
}

export const SQ_MONTHS = ['janar', 'shkurt', 'mars', 'prill', 'maj', 'qershor', 'korrik', 'gusht', 'shtator', 'tetor', 'nëntor', 'dhjetor']
export const SQ_WEEKDAYS = ['e diel', 'e hënë', 'e martë', 'e mërkurë', 'e enjte', 'e premte', 'e shtunë']

const pad2 = (n: number) => String(n).padStart(2, '0')

export interface DateOptions {
  /** Add the time of day (24 h). */
  time?: boolean
  /** Add the weekday. */
  weekday?: boolean
}

/** `3 nëntor 2026`, `3 nëntor 2026, 09:05`, `e martë, 3 nëntor 2026`; English through Intl. */
export function formatDate(
  value: string | Date,
  locale: Locale,
  options: DateOptions = {},
  supportsSq: boolean = intlSupportsAlbanian(),
): string {
  const d = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(d.getTime())) return '—'
  if (locale === 'sq' && !supportsSq) {
    const day = `${d.getDate()} ${SQ_MONTHS[d.getMonth()]} ${d.getFullYear()}`
    const head = options.weekday ? `${SQ_WEEKDAYS[d.getDay()]}, ${day}` : day
    return options.time ? `${head}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}` : head
  }
  return new Intl.DateTimeFormat(locale === 'sq' ? SQ_TAG : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(options.weekday ? { weekday: 'long' as const } : {}),
    ...(options.time ? { hour: '2-digit' as const, minute: '2-digit' as const, hour12: false } : {}),
  }).format(d)
}

/** Swap an English-formatted number's separators for Albanian ones: `1,234.50` → `1 234,50` (no-break space). */
function albanianNumber(english: string): string {
  return english.replace(/[.,]/g, (c) => (c === ',' ? NBSP : ','))
}

/**
 * A display price from the shared list (euros per minute) to the integer micro-euros the money helpers take.
 * `0.002` €/min is `2000` µ€/min; the display prices are exact to three decimals, so this never rounds away
 * a fraction of a micro-euro.
 */
export function eurPerMinuteToMicroEur(eurPerMinute: number): number {
  return Math.round(eurPerMinute * 1_000_000)
}

/** Euro amount from integer micro-euros: the shared formatEur, or its Albanian form (`0,1788 €`) without 'sq' data. */
export function formatMoney(microEur: number, locale: Locale, supportsSq: boolean = intlSupportsAlbanian()): string {
  if (locale === 'en' || supportsSq) return formatEur(microEur, locale)
  const english = formatEur(Math.abs(microEur), 'en') // "€0.1788"
  const sign = microEur < 0 ? '-' : ''
  return `${sign}${albanianNumber(english.replace('€', ''))}${NBSP}€`
}

/**
 * An amount to show as money to the cent (half-up on the magnitude): `0,5424 €` → `0,54 €`.
 * Non-zero amounts that round to nothing read `< 0,01 €` (`> -0,01 €` for a tiny debit), never `0,00 €`.
 * Use it for costs, balances and ledger lines; per-minute rates keep the exact `formatMoney`.
 */
export function formatMoneyRounded(microEur: number, locale: Locale, supportsSq: boolean = intlSupportsAlbanian()): string {
  const negative = microEur < 0
  const cents = Math.floor(Math.abs(microEur) / 10_000 + 0.5)
  const format = (value: number) => {
    if (locale === 'sq' && !supportsSq) {
      const english = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
      return `${albanianNumber(english.replace('€', ''))}${NBSP}€`
    }
    return new Intl.NumberFormat(locale === 'sq' ? SQ_TAG : 'en-IE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }
  if (cents === 0 && microEur !== 0) {
    return negative ? `> -${format(0.01)}` : `< ${format(0.01)}`
  }
  const amount = format(cents / 100)
  if (locale === 'sq' && !supportsSq) return negative ? `-${amount}` : amount
  return negative ? format(-cents / 100) : amount
}

/** US dollar price, for the competitor reference: `0,016 US$` in Albanian. */
export function formatUsd(amount: number, locale: Locale, supportsSq: boolean = intlSupportsAlbanian()): string {
  if (locale === 'sq' && !supportsSq) {
    const digits = new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(amount)
    return `${albanianNumber(digits)}${NBSP}US$`
  }
  return new Intl.NumberFormat(locale === 'sq' ? SQ_TAG : 'en-GB', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 3,
  }).format(amount)
}

export function formatNumber(n: number, locale: Locale, supportsSq: boolean = intlSupportsAlbanian()): string {
  if (locale === 'sq' && !supportsSq) {
    return albanianNumber(new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n))
  }
  return new Intl.NumberFormat(locale === 'sq' ? SQ_TAG : 'en-GB', { maximumFractionDigits: 1 }).format(n)
}

export type PluralCategory = 'one' | 'other'

/** Albanian and English both have `one` (exactly 1) and `other`; Intl.PluralRules decides, with a fallback. */
export function pluralCategory(locale: Locale, n: number, supportsSq: boolean = intlSupportsAlbanian()): PluralCategory {
  if (locale === 'sq' && !supportsSq) return n === 1 ? 'one' : 'other'
  try {
    return new Intl.PluralRules(locale === 'sq' ? SQ_TAG : 'en').select(n) === 'one' ? 'one' : 'other'
  } catch {
    return n === 1 ? 'one' : 'other'
  }
}
